import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
} from "@mui/material";
import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { IoMdClose } from "react-icons/io";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { toast } from "react-toastify";
import { date, object, string } from "yup";

import { useQueryClient } from "react-query";
import { useProject } from "../../../contexts/project.context";
import { useBudget } from "../../../contexts/BudgetContext/projectbudgets.context";
import { queryKeys } from "../../../queries/keys";
import { ProjectStatus } from "../../../types";
import {
  BRIEFING_STATUS_OPTIONS,
  ALLOWED_STATUS_TRANSITION,
  ALLOWED_STATUS_RETURN,
  TRANSITION_REQUIREMENTS,
  isSameStatusStay,
} from "../../../utils/briefingStatus";
import {
  getPendingManagementFieldsForStatus,
  getProjectStatusText,
} from "../../../utils/managementFields";
import DatePickerForm from "../../DatePickerForm";
import { Input } from "../../Input";
import SelectForm from "../../UI/Select";

interface ProjectStatusModalProps {
  isOpen: boolean;
  status?: ProjectStatus;
  onClose: () => void;
}

/** Descrição explicativa exibida abaixo do campo Status para status que podem gerar confusão. */
const STATUS_DESCRIPTIONS: Record<
  string,
  { title: string; description: string }
> = {
  LOST: {
    title: "Perdido selecionado",
    description:
      "O cliente optou por contratar outro fornecedor ou concorrente.",
  },
  CANCELED: {
    title: "Cancelado selecionado",
    description: "O cliente cancelou o evento e ele não será mais realizado.",
  },
  DECLINED: {
    title: "Declinado selecionado",
    description:
      "A MM Eventos optou por não participar ou atender este evento.",
  },
};

const schema = object({
  status: string().required("Status é obrigatório"),
  justification: string().required("Justificativa é obrigatório"),
  estimatedDate: date()
    .typeError("Data inválida")
    .required("Data previsão é obrigatório"),
}).required();

const MANAGEMENT_FIELDS_PENDING_CODE = "MANAGEMENT_FIELDS_PENDING";

/** Campos Gerenciais pendentes devolvidos pela API ao bloquear o status. */
function getManagementFieldsErrorPendencies(error: any): string[] {
  const data = error?.response?.data;
  return data?.code === MANAGEMENT_FIELDS_PENDING_CODE &&
    Array.isArray(data?.pendingFields)
    ? data.pendingFields
    : [];
}

function getStatusErrorPendencies(error: {
  pendencies?: string[];
  response?: { data?: { message?: string | string[] } };
}): string[] {
  if (error?.pendencies?.length) {
    return error.pendencies;
  }

  const apiMessage = error?.response?.data?.message;
  if (Array.isArray(apiMessage)) {
    return apiMessage.filter(
      (item): item is string => typeof item === "string" && item.length > 0
    );
  }

  if (typeof apiMessage === "string" && apiMessage.length > 0) {
    return apiMessage.split("\n").filter(Boolean);
  }

  return [];
}

const ProjectStatusModal = ({
  status,
  isOpen,
  onClose,
}: ProjectStatusModalProps) => {
  const queryClient = useQueryClient();
  const {
    project,
    updateStatus,
    summary,
    getStatusPendencies,
    client,
    event,
    goToPendingManagementFields,
  } = useProject();
  const { budgetList, closingControl, resolveProjectClosingStatus } =
    useBudget();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [pendencies, setPendencies] = React.useState<string[]>([]);
  const [isPendenciesOpen, setIsPendenciesOpen] = React.useState(false);
  const [pendingManagementFields, setPendingManagementFields] =
    React.useState<{ status: string; fields: string[] } | null>(null);
  const methods = useForm({
    defaultValues: {
      status: "",
      justification: "",
      estimatedDate: undefined,
    },
    resolver: yupResolver(schema),
    mode: "all",
  });

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = methods;

  async function onSubmit(form: any) {
    if (isSubmitting) return;

    const isSameStatus = isSameStatusStay(status?.status, form?.status);

    // Campos Gerenciais obrigatórios no status de destino bloqueiam a entrada
    // nele (a API valida de novo; aqui evita a ida ao servidor).
    if (!isSameStatus) {
      const pendingFields = getPendingManagementFieldsForStatus(
        client?.clientSelected?.management_fields,
        event?.managementFieldValues,
        form?.status
      );
      if (pendingFields.length > 0) {
        setPendingManagementFields({
          status: form?.status,
          fields: pendingFields,
        });
        return;
      }
    }

    // Coleta TODAS as pendências (resumo financeiro + endereço + orçamento em
    // Fechamento) em uma única lista, para exibir em um único modal.
    // Permanecer no mesmo status só atualiza data/justificativa — sem pendências.
    const collectedPendencies: string[] = [];

    if (!isSameStatus && form?.status === "APPROVED") {
      const hasFilledApproved =
        summary["APPROVED"]?.hasOwnProperty("totalValueOfEvents");
      if (!hasFilledApproved) {
        collectedPendencies.push(
          "O resumo financeiro do status aprovado precisa estar preenchido."
        );
      }
    }

    if (
      !isSameStatus &&
      status?.status === "PRE_CLOSING" &&
      form?.status === "CLEARED_OPERATIONS"
    ) {
      const hasFilledClosure =
        summary["CLOSURE"]?.hasOwnProperty("totalValueOfEvents");
      if (!hasFilledClosure) {
        collectedPendencies.push(
          "O resumo financeiro do status fechamento precisa estar preenchido"
        );
      }
    }

    const isReturnStatus = status
      ? ALLOWED_STATUS_RETURN?.[status?.status]?.includes(form?.status)
      : false;

    // O status de fechamento vem por orçamento e a query do contexto só roda na
    // guia Fechamento. Aqui o modal pode estar em qualquer guia (Dados
    // Cadastrais, por exemplo), então resolvemos o status pelos orçamentos em
    // Fechamento antes de validar — senão a ida a Pré-Fechamento é barrada mesmo
    // com o orçamento já em Pré Fechamento.
    const closingStatus =
      !isSameStatus &&
      !isReturnStatus &&
      TRANSITION_REQUIREMENTS[form?.status]?.requiresPreClosing &&
      resolveProjectClosingStatus
        ? await resolveProjectClosingStatus()
        : closingControl?.closingStatus;

    // Pendências de endereço + orçamento em Fechamento (somente em avanço de status)
    if (!isSameStatus && !isReturnStatus) {
      collectedPendencies.push(
        ...getStatusPendencies(form?.status, budgetList, closingStatus)
      );
    }

    // Havendo qualquer pendência, abre o único modal e não avança o status
    if (collectedPendencies.length > 0) {
      setPendencies(collectedPendencies);
      setIsPendenciesOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await updateStatus(
        { ...form, _id: status?._id },
        isReturnStatus,
        budgetList,
        closingStatus
      );
      if (project?._id) {
        const projectId = project._id;
        await queryClient.invalidateQueries(
          queryKeys.projectTimelineAudit(projectId)
        );
        await queryClient.invalidateQueries(
          queryKeys.projectLastEditorAudit(projectId)
        );
        window.setTimeout(() => {
          queryClient.invalidateQueries(
            queryKeys.projectTimelineAudit(projectId)
          );
        }, 2500);
      }
      toast.success("Status atualizado com sucesso");
      onClose();
    } catch (error: any) {
      const apiPendingFields = getManagementFieldsErrorPendencies(error);
      if (apiPendingFields.length > 0) {
        setPendingManagementFields({
          status: form?.status,
          fields: apiPendingFields,
        });
        return;
      }
      const apiPendencies = getStatusErrorPendencies(error);
      if (apiPendencies.length > 0) {
        setPendencies(apiPendencies);
        setIsPendenciesOpen(true);
      } else {
        toast.error("Erro ao alterar o status");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const allowedTransitions = status
    ? ALLOWED_STATUS_TRANSITION[status?.status]
      ? [status?.status, ...ALLOWED_STATUS_TRANSITION[status?.status]]
      : [status?.status]
    : [];

  const filteredStatusOptions = BRIEFING_STATUS_OPTIONS.filter((item) =>
    allowedTransitions.includes(item.value)
  );

  useEffect(() => {
    if (status) {
      const parsedDate = status.estimatedDate
        ? new Date(status.estimatedDate)
        : undefined;
      reset({
        status: status.status,
        justification: "",
        estimatedDate:
          parsedDate && !Number.isNaN(parsedDate.getTime())
            ? parsedDate
            : undefined,
      });
    }
  }, []);
  return (
    <FormProvider {...methods}>
      <Dialog onClose={onClose} open={isOpen} fullWidth maxWidth={"sm"}>
        <DialogTitle sx={{ m: 0, px: 4, py: 3, color: "#1361A4" }}>
          Alterar Status
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <IoMdClose />
        </IconButton>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ px: 4 }}>
            <Box display={"flex"} flexDirection={"column"} gap={2}>
              <Box display={"flex"} gap={4}>
                <SelectForm
                  label="Status *"
                  value={watch("status")}
                  {...register("status")}
                  error={!!errors.status}
                  helperText={errors.status?.message}
                  customLabel
                >
                  {filteredStatusOptions.map(({ value, text }, index) => (
                    <MenuItem key={index} value={value}>
                      {text}
                    </MenuItem>
                  ))}
                </SelectForm>

                <DatePickerForm
                  onBlur={() => {}}
                  label="Data  previsão *"
                  name="estimatedDate"
                  control={control}
                  error={errors.estimatedDate?.message}
                />
              </Box>

              {STATUS_DESCRIPTIONS[watch("status")] && (
                <Alert severity="info">
                  <AlertTitle>
                    {STATUS_DESCRIPTIONS[watch("status")].title}
                  </AlertTitle>
                  {STATUS_DESCRIPTIONS[watch("status")].description}
                </Alert>
              )}

              <Input
                label="Justificativa *"
                {...register("justification")}
                error={!!errors.justification}
                helperText={errors.justification?.message}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 4, py: 3 }}>
            <Button
              type="submit"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={20} /> : undefined
              }
            >
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={isPendenciesOpen}
        onClose={() => setIsPendenciesOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ m: 0, px: 4, py: 3, color: "#1361A4" }}>
          Pendências para aprovar o projeto
        </DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          <Box
            component="ul"
            sx={{
              pl: 3,
              m: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {pendencies.map((message, index) => (
              <li key={index} style={{ whiteSpace: "pre-line" }}>
                {message}
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3 }}>
          <Button onClick={() => setIsPendenciesOpen(false)}>Entendi</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pendingManagementFields}
        onClose={() => setPendingManagementFields(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ m: 0, px: 4, pt: 3, display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <ErrorOutlineIcon color="error" fontSize="large" />
          Campos obrigatórios pendentes
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setPendingManagementFields(null)}
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <IoMdClose />
        </IconButton>
        <DialogContent sx={{ px: 4 }}>
          <Box mb={1}>
            Para alterar o projeto para "
            {getProjectStatusText(pendingManagementFields?.status)}", preencha
            os seguintes campos gerenciais:
          </Box>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {pendingManagementFields?.fields.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setPendingManagementFields(null)}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              goToPendingManagementFields(pendingManagementFields?.fields ?? []);
              setPendingManagementFields(null);
              onClose();
            }}
          >
            Ir para Dados Cadastrais
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default ProjectStatusModal;
